import { faArrowsRotate, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { Group } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ITemplate,
  SingularEmailBaseCampaign,
  TEmailCampaignMailing,
  Template,
} from '@feathr/blackbox';
import { CampaignClass, CampaignState, TemplateClass } from '@feathr/blackbox';
import {
  Button,
  EmailTemplateSelect as EmailTemplateSelectComponent,
  Icon,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import type { TTemplateSelectAndEditState } from '../TemplateSelectAndEditModal';
import TemplateSelectAndEditModal from '../TemplateSelectAndEditModal';

import * as styles from './EmailTemplateSelect.css';

interface IProps {
  campaign: SingularEmailBaseCampaign;
  disabled?: boolean;
  /**
   * The model that owns the `template_id` being written. Defaults to `campaign`.
   * Pass any {@link TEmailCampaignMailing} (e.g. an ephemeral drip step, which has no
   * `addTemplate` and is never saved on its own) to retarget the write while still
   * cloning/keying the template on the parent `campaign`.
   */
  mailing?: TEmailCampaignMailing;
  /** Update template before saving */
  onPreChangeTemplate?: (template: Template, patch: Partial<ITemplate>) => Partial<ITemplate>;
  template?: Template;
}

function EmailTemplateSelect({
  campaign,
  disabled,
  mailing,
  onPreChangeTemplate,
  template: originalTemplate,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();
  const [template, setTemplate] = useState(originalTemplate);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();

  const disclosure = useDisclosure();
  const [, { open: openTemplateModal }] = disclosure;

  const [initialState, setInitialState] = useState<TTemplateSelectAndEditState | undefined>();
  const isDraft = campaign.get('state') === CampaignState.Draft;

  useEffect(() => {
    setThumbnailUrl(
      isDraft && template ? template.get('thumbnail_url') + `?cb=${Date.now()}` : undefined,
    );
  }, [template, template?.isAttributeDirty('content')]);

  // The mailing whose template_id should be updated; defaults to the campaign.
  const targetMailing = mailing ?? campaign;

  async function handleTemplateChange(templateId?: string): Promise<void> {
    if (!templateId) {
      // Remove template
      targetMailing.set({ template_id: undefined });
      return;
    }

    /*
     * Persist non-drip Pinpoint campaigns before cloning the template. With Mailrail,
     * we are trying to move away from saving changes without the user's permission,
     * and ephemeral Pinpoint drip-step specs can't be saved on their own.
     */
    if (campaign.isPinpoint && campaign.get('_cls') !== CampaignClass.DripStep) {
      await campaign.patchDirty();
    }

    // Duplicate template, attach to mailing, update name to match campaign.
    // Templates are always keyed on the top-level campaign object.
    const duplicatedTemplate = await Templates.clone(templateId, {
      campaign_id: campaign.get('parent_id') || campaign.id,
      event_id: campaign.get('event'),
      _cls: TemplateClass.SESEmail,
    });
    setTemplate(duplicatedTemplate);
    // Name the cloned template after the mailing (e.g. the drip step) when it has a name,
    // falling back to the campaign name.
    const templateName = mailing?.get('name') || campaign.get('name');
    let templatePatch: Partial<ITemplate> = { name: templateName };
    // Allow implementing components to modify template before it is saved
    if (onPreChangeTemplate) {
      templatePatch = onPreChangeTemplate(duplicatedTemplate, templatePatch);
    }
    duplicatedTemplate.set(templatePatch);
    await duplicatedTemplate.patchDirty();

    if ('setTemplate' in targetMailing) {
      // Top-level campaign path: attach (and persist when not ephemeral) the template.
      await targetMailing.setTemplate(duplicatedTemplate);
    } else {
      // Ephemeral/embedded mailing: no addTemplate and no save — just record the id.
      targetMailing.set({ template_id: duplicatedTemplate.id });
    }
  }

  // Open template modal for editing
  function handleEditTemplate(): void {
    setInitialState('editing');
    openTemplateModal();
  }

  // Open template modal for selecting a new template
  function handleChooseTemplate(): void {
    setInitialState('selecting');
    openTemplateModal();
  }

  return (
    <EmailTemplateSelectComponent
      description={
        template ? template?.get('preview_text') : t('Get started by selecting a template')
      }
      imageUrl={thumbnailUrl}
      isLoading={template?.isPending}
      previewUrl={template && !isDraft ? campaign.getTemplatePreviewUrl() : undefined}
      theme={'neutral'}
      title={template === undefined ? t('No template selected') : template.name}
    >
      {template === undefined ? (
        <Button disabled={disabled} onClick={openTemplateModal}>
          {t('Select template')}
        </Button>
      ) : (
        <Group className={styles.actions} grow={true} justify={'space-between'}>
          <Button
            disabled={disabled || template.isReadOnly}
            onClick={handleEditTemplate}
            prefix={<Icon icon={faPenToSquare} />}
          >
            {t('Edit content')}
          </Button>
          <Button
            // The campaign must be stopped before changing its template
            // This ensures that the existing template is properly deleted from SES and the new template is properly upsynced
            disabled={disabled || campaign.isPublished}
            onClick={handleChooseTemplate}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={
              campaign.isMailrailAutoSend && campaign.isPublished
                ? t('The campaign must be stopped before changing its template')
                : undefined
            }
          >
            {t('Change template')}
          </Button>
        </Group>
      )}
      <TemplateSelectAndEditModal
        disclosure={disclosure}
        initialState={initialState}
        onChange={handleTemplateChange}
        templateClass={TemplateClass.SESEmail}
        templateId={template?.id}
      />
    </EmailTemplateSelectComponent>
  );
}

export default observer(EmailTemplateSelect);
